// The two contracts the capacitor-sqlite adapter uses to report a problem it hit
// on the device, and the factories that build them on top of a LoggingService.
//
// They are kept apart on purpose. One is for an INCONSISTENCY found in the
// stored data — a document whose winning revision has lost its body row, say —
// and the other is for an OPERATION that failed — a SQL read that errored, a
// caller-supplied filter that threw. Those are different questions with
// different answers, so they get different report names and never share a
// Sentry group.
//
// This lives in its own file rather than inside adapter.ts so that the services
// which only want the reporter do not have to depend on the whole adapter
// implementation. That matters here more than it usually would: the adapter
// cannot import LoggingService (database-recovery.service.ts imports from this
// directory and logging.service.ts imports that, so it would close a cycle),
// and every service that depends on adapter.ts is one
// more module adapter.ts can never reach.

/** Reports an inconsistency this adapter found in the on-device database
 *  (#4417). Declared once, here, and imported by whoever supplies it — the
 *  value crosses into the adapter through PouchDB's untyped constructor
 *  options, so without a shared type the two ends could drift and still
 *  compile.
 *
 *  `severity` picks the telemetry tier (#4382) by CLAUDE.md's rule — is the
 *  condition still unresolved? 'info' is used ONLY where the repair has
 *  already landed by the time the call runs; 'problem' is a sighting nothing
 *  has fixed. */
export type LocalCorruptionReporter =
  (name: string, context: Record<string, unknown>, severity: 'info' | 'problem') => void;

/** Builds the reporter above on top of a LoggingService, routing each severity
 *  to its tier. Deliberately takes only the two methods it uses rather than
 *  LoggingService itself: `database-recovery.service.ts` imports from this directory
 *  and `logging.service.ts` imports that, so naming that class here would close an
 *  import cycle.
 *
 *  Lives beside the type so every local database reports the same way — the
 *  user database, the shared ones, the coach exercise staging database and the
 *  image upload outbox all run this same adapter, so corruption in any of them
 *  is worth the same attention. */
export function makeLocalCorruptionReporter(
  logging: { trackSilentInfo(name: string, context: any): void; trackSilentProblem(name: string, context: any): void },
  databaseLabel: string,
): LocalCorruptionReporter {
  return (name, context, severity) => {
    const reportName = `SqliteLocalCorruption: ${name}`;
    const reportContext = { database: databaseLabel, ...context };
    if (severity === 'info') {
      logging.trackSilentInfo(reportName, reportContext);
    } else {
      logging.trackSilentProblem(reportName, reportContext);
    }
  };
}

/** Reports an OPERATION of this adapter that failed, as opposed to an
 *  inconsistency it found in the stored data (which is
 *  `LocalCorruptionReporter` above). The live changes feed is the first caller:
 *  it polls in the background, so an error from one poll has no caller to
 *  return to and used to be dropped on the floor.
 *
 *  There is no `severity` parameter. A failed operation is by definition still
 *  unresolved at the moment it is reported — nothing repaired it — so by
 *  CLAUDE.md's #4382 rule it is always the 'problem' tier. The corruption
 *  reporter needs the parameter because it has genuine 'info' callers: a repair
 *  that has already committed. */
export type LocalAdapterFaultReporter =
  (name: string, context: Record<string, unknown>) => void;

/** Builds the fault reporter above on top of a LoggingService. Takes only the
 *  one method it uses, for the same import-cycle reason as
 *  `makeLocalCorruptionReporter`.
 *
 *  Wired for every local database that runs this adapter — the user database,
 *  the officer's shared ones, the coach exercise staging database and the image
 *  upload outbox — so a failure in any of them is equally visible, and the
 *  report says which one it was. */
export function makeLocalAdapterFaultReporter(
  logging: { trackSilentProblem(name: string, context: any): void },
  databaseLabel: string,
): LocalAdapterFaultReporter {
  return (name, context) => {
    logging.trackSilentProblem(`SqliteAdapterFault: ${name}`, { database: databaseLabel, ...context });
  };
}
